import { scanContentSeverity } from "./severityScanner.js";

/** @typedef {import('../domain/types.js').BehavioralCategory} BehavioralCategory */
/** @typedef {import('./severityScanner.js').ContentSeverity} ContentSeverity */

/**
 * @typedef {Object} HeuristicPattern
 * @property {RegExp} re match package or display label
 * @property {BehavioralCategory} category
 * @property {ContentSeverity} [severityHint] immediate severity if matched on name/pkg
 * @property {import('../domain/types.js').TriggerType[]} [triggerTypes]
 */

/** Ordered: first match wins. */
/** @type {HeuristicPattern[]} */
export const HEURISTIC_PATTERNS = [
  // manga / manhua / webtoon
  { re: /toomics/i, category: "manga_reading", severityHint: "flagged", triggerTypes: ["sexualized_content_risk", "escapism_media"] },
  { re: /webtoon/i, category: "manga_reading", triggerTypes: ["escapism_media"] },
  { re: /manta\.comics|manta\.app|\bmanta\b/i, category: "manga_reading", triggerTypes: ["escapism_media"] },
  { re: /tapas/i, category: "manga_reading", triggerTypes: ["escapism_media"] },
  { re: /lezhin|piccoma|webnovel|kakao.?page|linewebtoon/i, category: "manga_reading", triggerTypes: ["escapism_media"] },
  { re: /mangatoon|mangapark|mangadex|comixology/i, category: "manga_reading", triggerTypes: ["escapism_media"] },
  { re: /tachiyomi|mihon|kotatsu/i, category: "manga_reading", triggerTypes: ["escapism_media"] },
  { re: /manhua|manga|comics?.reader|webtoon/i, category: "manga_reading", triggerTypes: ["escapism_media"] },
  // anime streaming
  { re: /crunchyroll/i, category: "anime_streaming", triggerTypes: ["escapism_media"] },
  { re: /funimation/i, category: "anime_streaming", triggerTypes: ["escapism_media"] },
  { re: /bilibili/i, category: "anime_streaming", triggerTypes: ["escapism_media"] },
  { re: /kissanime|9anime|gogoanime|anime.?flix/i, category: "anime_streaming", severityHint: "flagged", triggerTypes: ["sexualized_content_risk", "escapism_media"] },
  // short-form
  { re: /tiktok|musically|instagram|reels|shorts|snapchat|pinterest|threads/i, category: "shortform_feed", triggerTypes: ["compulsive_scroll", "novelty_feed"] },
  { re: /notion|todoist|evernote|asana|trello/i, category: "productivity", triggerTypes: ["none"] },
  { re: /news|nytimes|bbc|cnn|reuters|flipboard/i, category: "utility", triggerTypes: ["none"] },
  { re: /amazon|ebay|etsy|shopify|aliexpress|wish\.com/i, category: "utility", triggerTypes: ["none"] },
  // comms / utility
  { re: /whatsapp|telegram|signal|messenger|facebook\.orca/i, category: "work_comms", triggerTypes: ["none"] },
  { re: /slack|teams|zoom|outlook|gmail/i, category: "work_comms", triggerTypes: ["none"] },
  // navigation / banking
  { re: /waze|maps\.google|navigation|gps/i, category: "navigation", triggerTypes: ["none"] },
  { re: /bank|chase|wellsfargo|paypal|venmo/i, category: "banking", triggerTypes: ["none"] },
  // gaming / entertainment
  { re: /game|steam|epicgames|roblox/i, category: "gaming_session", triggerTypes: ["escapism_media"] },
  { re: /netflix|hulu|disney|primevideo|spotify/i, category: "entertainment_discovery", triggerTypes: ["escapism_media"] },
  // explicit pkg/name signals
  { re: /hentai|ecchi|nsfw|xxx|porn|onlyfans/i, category: "unknown", severityHint: "flagged", triggerTypes: ["sexualized_content_risk"] },
];

/**
 * Layer 1 — instant offline classification from package + label.
 * @param {string} packageName
 * @param {string} displayName
 * @returns {{
 *   primaryCategory: BehavioralCategory,
 *   contentSeverity: ContentSeverity,
 *   triggerTypes: import('../domain/types.js').TriggerType[],
 *   matchedPatterns: string[],
 *   severityHits: string[],
 *   confidence: number,
 * }}
 */
export function classifyHeuristic(packageName, displayName) {
  const hay = `${packageName} ${displayName}`.trim();
  if (!hay) {
    return {
      primaryCategory: "unknown",
      contentSeverity: "unknown",
      triggerTypes: ["unknown"],
      matchedPatterns: [],
      severityHits: [],
      confidence: 0.1,
    };
  }

  for (const p of HEURISTIC_PATTERNS) {
    if (p.re.test(hay)) {
      const severityScan = scanHeuristicSeverity(hay);
      const contentSeverity = p.severityHint
        ? mergeSeverityHint(p.severityHint, severityScan.severity)
        : severityScan.severity;
      return {
        primaryCategory: p.category,
        contentSeverity,
        triggerTypes: p.triggerTypes || ["none"],
        matchedPatterns: [String(p.re)],
        severityHits: severityScan.hits,
        confidence: 0.75,
      };
    }
  }

  const severityScan = scanHeuristicSeverity(hay);
  return {
    primaryCategory: "unknown",
    contentSeverity: severityScan.severity,
    triggerTypes: severityScan.severity === "flagged" ? ["sexualized_content_risk"] : ["unknown"],
    matchedPatterns: [],
    severityHits: severityScan.hits,
    confidence: 0.25,
  };
}

/**
 * @param {string} hay
 */
function scanHeuristicSeverity(hay) {
  return scanContentSeverity(hay);
}

/**
 * @param {ContentSeverity} hint
 * @param {ContentSeverity} scanned
 */
function mergeSeverityHint(hint, scanned) {
  if (hint === "flagged" || scanned === "flagged") return "flagged";
  if (hint === "unknown" || scanned === "unknown") return "unknown";
  return "clean";
}
